/**
 * Browser replacement for the Electron preload script (src/preload/src/index.ts).
 *
 * Installs `window.api` and `window.versions` backed by browser facilities
 * instead of IPC to the Electron main process:
 *  - state persistence -> localStorage (per hive, diff ops applied locally)
 *  - stylesheet compilation -> precompiled theme.css fetched once
 *  - everything OS/window/process related -> fused off (no-ops with sane
 *    return values)
 *
 * Must be imported before the renderer entry module evaluates.
 */
import { pickDirectoryMount, pickFileMount } from "./fsa-mounts";
import { WEB_GAMES } from "./web-games";
const WEB_VERSION = "1.0.0-web";
const STORAGE_PREFIX = "vortex-web-state/";
// hives the desktop app persists; anything else lives only in memory
const DEFAULT_HIVES = ["app", "settings", "persistent", "confidential", "user"];
// ---------------------------------------------------------------------------
// persistence: localStorage-backed hive store
// ---------------------------------------------------------------------------
const hiveCache = {};
const dirtyHives = new Set();
let flushTimer = null;
function loadHive(hive) {
    if (hiveCache[hive] === undefined) {
        try {
            const raw = window.localStorage.getItem(STORAGE_PREFIX + hive);
            hiveCache[hive] = raw !== null ? JSON.parse(raw) : {};
        }
        catch (err) {
            console.warn(`[vortex-web] discarding corrupt persisted hive '${hive}'`, err);
            hiveCache[hive] = {};
        }
    }
    return hiveCache[hive];
}
function scheduleFlush() {
    if (flushTimer !== null) {
        return;
    }
    flushTimer = window.setTimeout(() => {
        flushTimer = null;
        for (const hive of dirtyHives) {
            try {
                window.localStorage.setItem(STORAGE_PREFIX + hive, JSON.stringify(hiveCache[hive]));
            }
            catch (err) {
                console.warn(`[vortex-web] failed to persist hive '${hive}'`, err);
            }
        }
        dirtyHives.clear();
    }, 250);
}
function applyOperation(target, op) {
    const path = op.path;
    if (path.length === 0) {
        return;
    }
    let node = target;
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (typeof node[key] !== "object" || node[key] === null) {
            if (op.type === "remove") {
                return;
            }
            node[key] = {};
        }
        node = node[key];
    }
    const leaf = path[path.length - 1];
    if (op.type === "set") {
        node[leaf] = op.value;
    }
    else {
        delete node[leaf];
    }
}
// ---------------------------------------------------------------------------
// stylesheets: precompiled at build time, fetched once
// ---------------------------------------------------------------------------
let themeCssPromise = null;
function fetchThemeCss() {
    if (themeCssPromise === null) {
        themeCssPromise = fetch("assets/css/theme.css")
            .then((res) => (res.ok ? res.text() : ""))
            .catch((err) => {
            console.warn("[vortex-web] failed to fetch precompiled theme", err);
            return "";
        });
    }
    return themeCssPromise;
}
// ---------------------------------------------------------------------------
// fake paths handed to the renderer; the fs shim answers all of them with
// ENOENT/empty listings, which the renderer treats as "nothing there"
// ---------------------------------------------------------------------------
const vortexPaths = {
    base: "/vortex",
    base_unpacked: "/vortex",
    assets: "/vortex/assets",
    assets_unpacked: "/vortex/assets",
    modules: "/vortex/modules",
    modules_unpacked: "/vortex/modules",
    bundledPlugins: "/vortex/bundledPlugins",
    locales: "/vortex/locales",
    package: "/vortex",
    package_unpacked: "/vortex",
    // kept distinct from (not a parent of) userData so the staging-folder
    // validity check doesn't flag the staging dir as "inside the app dir"
    application: "/vortex/app",
    userData: "/vortex/userData",
    appData: "/vortex/appData",
    localAppData: "/vortex/localAppData",
    temp: "/vortex/temp",
    home: "/home/web",
    documents: "/home/web/documents",
    exe: "/vortex/vortex-web",
    desktop: "/home/web/desktop",
};
// ---------------------------------------------------------------------------
const noopUnsubscribe = () => () => undefined;
const consoleLevel = {
    debug: "debug",
    info: "info",
    warn: "warn",
    error: "error",
};
// ---------------------------------------------------------------------------
// game launching: the renderer's Play button ends in child_process.spawn of
// the game executable. The child_process shim consults this resolver — when
// the spawned path IS a discovered game directory of a curated Steam game,
// the launch is redirected to a steam:// deep link, which the browser hands
// to the locally installed Steam client.
// ---------------------------------------------------------------------------
function gameIdFromUri(gameUri) {
    return gameUri.replace(/^game:/, "");
}
function resolveSteamLaunchUrl(commandPath) {
    const settings = loadHive("settings");
    const discovered = settings?.gameMode?.discovered ?? {};
    const norm = String(commandPath)
        .replace(/^"+|"+$/g, "")
        .replace(/\\/g, "/")
        .replace(/\/+$/, "");
    for (const [gameId, disco] of Object.entries(discovered)) {
        const discoPath = disco?.path?.replace(/\/+$/, "");
        // exact match only: the game starter's exePath resolves to the game
        // directory itself (adaptor games use executable "."); deeper paths are
        // tools, which stay fused off
        if (discoPath === undefined || norm !== discoPath) {
            continue;
        }
        const entry = WEB_GAMES.find((game) => gameIdFromUri(game.gameInfo.gameUri) === gameId);
        const appId = entry?.gameInfo.steam?.[0]?.appId;
        if (appId !== undefined) {
            return `steam://rungameid/${appId}`;
        }
    }
    return null;
}
globalThis.__vortexWebLaunch = {
    resolveSteamLaunchUrl,
};
const api = {
    log: (level, message, metadata) => {
        const fn = consoleLevel[level] ?? "log";
        // eslint-disable-next-line no-console
        console[fn](`[vortex] ${message}`, metadata ?? "");
    },
    compileStylesheets: (_filePaths) => fetchThemeCss(),
    example: {
        ping: () => Promise.resolve("pong (web)"),
    },
    shell: {
        openUrl: (url) => {
            window.open(url, "_blank", "noopener");
        },
        openFile: (_filePath) => {
            console.warn("[vortex-web] shell.openFile is fused off in the web build");
        },
    },
    persist: {
        sendDiff: (hive, operations) => {
            const data = loadHive(hive);
            for (const op of operations) {
                applyOperation(data, op);
            }
            dirtyHives.add(hive);
            scheduleFlush();
        },
        getHydration: () => {
            const result = {};
            for (const hive of DEFAULT_HIVES) {
                const data = loadHive(hive);
                if (Object.keys(data).length > 0) {
                    result[hive] = data;
                }
            }
            return Promise.resolve(result);
        },
        onHydrate: (_callback) => undefined,
        onPush: (_callback) => undefined,
    },
    extensions: {
        // in Electron this kicks off main-process extension init; no main process here
        initializeAllMain: (_installType) => undefined,
    },
    adaptors: {
        // curated game definitions; the bundled adaptor_bridge extension
        // turns these into registerGame calls (info-only entries, so the
        // service-call APIs below are never reached)
        list: () => Promise.resolve(WEB_GAMES.map(({ gameInfo: _info, ...rest }) => rest)),
        listWithInfoSync: () => WEB_GAMES,
        call: () => Promise.reject(new Error("game adaptors are fused off in the web build")),
        buildSnapshot: () => Promise.reject(new Error("game adaptors are fused off in the web build")),
        detectVersion: () => Promise.reject(new Error("game adaptors are fused off in the web build")),
    },
    updater: {
        getStatus: () => Promise.resolve({ available: false, downloaded: false }),
        setChannel: () => undefined,
        checkForUpdates: () => undefined,
        downloadUpdate: () => undefined,
        restartAndInstall: () => undefined,
    },
    dialog: {
        showOpen: async (options) => {
            // directory + file selection are backed by the File System Access API
            if (options?.properties?.includes("openDirectory")) {
                const mountPath = await pickDirectoryMount();
                return mountPath !== null
                    ? { canceled: false, filePaths: [mountPath] }
                    : { canceled: true, filePaths: [] };
            }
            if (options?.properties?.includes("openFile")) {
                const filePath = await pickFileMount();
                return filePath !== null
                    ? { canceled: false, filePaths: [filePath] }
                    : { canceled: true, filePaths: [] };
            }
            return { canceled: true, filePaths: [] };
        },
        showSave: (_options) => Promise.resolve({ canceled: true, filePath: undefined }),
        showMessageBox: (options) => {
            console.info("[vortex-web] auto-answering message box", options?.message);
            return Promise.resolve({
                response: options?.defaultId ?? 0,
                checkboxChecked: false,
            });
        },
        showErrorBox: (title, content) => {
            console.error(`[vortex-web] ${title}: ${content}`);
            return Promise.resolve();
        },
    },
    app: {
        relaunch: (_args) => {
            window.location.reload();
        },
        getInitMetadata: () => Promise.resolve({
            commandLine: {},
            installType: "regular",
            version: WEB_VERSION,
            instanceId: "web",
            warnedAdmin: 1,
        }),
        setProtocolClient: () => Promise.resolve(),
        isProtocolClient: () => Promise.resolve(false),
        removeProtocolClient: () => Promise.resolve(),
        exit: (exitCode) => {
            console.warn(`[vortex-web] app.exit(${exitCode}) ignored in the web build`);
            return Promise.resolve();
        },
        getName: () => Promise.resolve("Vortex Web"),
        getPath: (name) => Promise.resolve(vortexPaths[name] ?? "/vortex"),
        extractFileIcon: () => Promise.resolve(),
        setJumpList: () => Promise.resolve(),
        setLoginItemSettings: () => Promise.resolve(),
        getLoginItemSettings: () => Promise.resolve({ openAtLogin: false, openAsHidden: false }),
        getAppPath: () => Promise.resolve("/vortex"),
        getVersion: () => Promise.resolve(WEB_VERSION),
        getVortexPaths: () => Promise.resolve(vortexPaths),
    },
    browserView: {
        create: () => Promise.resolve("web-noop-view"),
        createWithEvents: () => Promise.resolve("web-noop-view"),
        close: () => Promise.resolve(),
        position: () => Promise.resolve(),
        updateURL: () => Promise.resolve(),
    },
    session: {
        getCookies: () => Promise.resolve([]),
    },
    window: {
        getId: () => Promise.resolve(1),
        minimize: () => Promise.resolve(),
        maximize: () => Promise.resolve(),
        unmaximize: () => Promise.resolve(),
        restore: () => Promise.resolve(),
        close: () => Promise.resolve(),
        focus: () => Promise.resolve(),
        show: () => Promise.resolve(),
        hide: () => Promise.resolve(),
        isMaximized: () => Promise.resolve(false),
        isMinimized: () => Promise.resolve(false),
        isFocused: () => Promise.resolve(document.hasFocus()),
        setAlwaysOnTop: () => Promise.resolve(),
        moveTop: () => Promise.resolve(),
        onClose: noopUnsubscribe(),
        onFocus: (callback) => {
            window.addEventListener("focus", callback);
            return () => window.removeEventListener("focus", callback);
        },
        onBlur: (callback) => {
            window.addEventListener("blur", callback);
            return () => window.removeEventListener("blur", callback);
        },
        onResized: (callback) => {
            const listener = () => callback(window.innerWidth, window.innerHeight);
            window.addEventListener("resize", listener);
            return () => window.removeEventListener("resize", listener);
        },
        onMoved: (_callback) => () => undefined,
        onMaximized: (_callback) => () => undefined,
        getPosition: () => Promise.resolve([0, 0]),
        setPosition: () => Promise.resolve(),
        getSize: () => Promise.resolve([window.innerWidth, window.innerHeight]),
        setSize: () => Promise.resolve(),
        isVisible: () => Promise.resolve(true),
        toggleDevTools: () => Promise.resolve(),
    },
    menu: {
        onMenuClick: (_callback) => () => undefined,
        setApplicationMenu: (_template) => Promise.resolve(),
    },
    contentTracing: {
        startRecording: () => Promise.resolve(),
        stopRecording: () => Promise.resolve(""),
    },
    redux: {
        getState: () => Promise.resolve({}),
        getStateMsgpack: () => Promise.resolve(new Uint8Array()),
    },
    clipboard: {
        writeText: (text) => {
            if (navigator.clipboard !== undefined) {
                return navigator.clipboard.writeText(text).catch(() => undefined);
            }
            return Promise.resolve();
        },
        readText: () => Promise.resolve(""),
    },
    powerSaveBlocker: {
        start: () => Promise.resolve(1),
        stop: () => Promise.resolve(),
        isStarted: () => Promise.resolve(false),
    },
    telemetry: {
        forwardSpan: (_span) => undefined,
    },
    downloader: {
        start: () => Promise.reject(new Error("downloads are fused off in the web build")),
        pause: () => Promise.resolve(),
        resume: () => Promise.reject(new Error("downloads are fused off in the web build")),
        cancel: () => Promise.resolve(),
        getState: () => Promise.resolve(null),
        getStates: () => Promise.resolve([]),
        onResolve: (_handler) => () => undefined,
    },
};
window.api = api;
window.versions = {
    chromium: "0.0.0-web",
    electron: "0.0.0-web",
    node: "0.0.0-web",
};
